/* eslint-disable react-hooks/exhaustive-deps, react/jsx-no-bind, react/jsx-one-expression-per-line -- the effects here deliberately omit react-hook-form identities, which are stable */
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { orderBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { Children, useMemo, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useGetDisposalSitesMutation } from 'api/settings/getDisposalSites'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import { MONTHS } from 'settings/constants/date'
import T from 'T'
import { captureError, handleErrorWithMessage } from 'utils/error'
import { get } from 'utils/lodash'

import { useWorkOrderFacets } from '../useWorkOrderFacets'
import { getCurrentYearAndMonthFromPeriods, getLatestYearAndMonthFromPeriods, groupPeriodsByYear } from '../utils'
import WorkOrderSkeleton from '../WorkOrderSkeleton'

import MonthAccordion from './MonthAccordion'
import WorkOrderList from './WorkOrderList'
import YearAccordion from './YearAccordion'

const WorkOrderLayout = ({ accountId, locationId, configuredServiceId, noWOText = T.NO_WORK_ORDERS, hasFilters = false }) => {
  const [getDisposalSites, { data: disposalSites, isLoading: isDisposalSitesLoading }] = useGetDisposalSitesMutation()
  const allSites = useMemo(() => get(disposalSites, 'sites', []), [disposalSites])

  const [expandedYear, setExpandedYear] = useState([])
  const [expandedMonth, setExpandedMonth] = useState([])
  const { setValue, watch, getValues } = useFormContext()
  const filtersInitializedRef = useRef(false)

  const selectedServices = watch('selectedServices')
  const selectedRoutes = watch('selectedRoutes')
  const selectedWorkOrderStatuses = watch('selectedWorkOrderStatuses')
  const selectedDateRange = watch('selectedDateRange')

  const { servicesOptions, routesOptions, routeCounts, periods, totalCount, isFetched, isError, isOptionsError, error, optionsError } =
    useWorkOrderFacets({
      accountId,
      locationId,
      configuredServiceId,
      selectedServices,
      selectedRoutes,
      selectedWorkOrderStatuses,
      selectedDateRange,
    })

  const isLoading = isDisposalSitesLoading || !isFetched

  // Apart, since either can fail while the other already has: the list has a placeholder, the dropdowns don't.
  const reportedListError = useRef(null)
  useEffect(() => {
    if (!isError) {
      reportedListError.current = null
      return
    }
    if (reportedListError.current === error) return
    reportedListError.current = error
    captureError(error, { surface: 'work-order-list' })
  }, [isError, error])

  // Until a filter narrows one of them both calls share a cache entry, so the same object is one failure.
  const reportedOptionsError = useRef(null)
  useEffect(() => {
    if (!isOptionsError || optionsError === error) {
      reportedOptionsError.current = null
      return
    }
    if (reportedOptionsError.current === optionsError) return
    reportedOptionsError.current = optionsError
    handleErrorWithMessage(optionsError, T.FAILED_TO_LOAD_SERVICES)
  }, [isOptionsError, optionsError, error])

  const periodsByYear = useMemo(() => groupPeriodsByYear(periods), [periods])
  const sortedDisplayedYears = useMemo(() => orderBy(Object.keys(periodsByYear), year => year, 'desc'), [periodsByYear])
  const hasDisplayedSummary = sortedDisplayedYears.length > 0
  const handleYearChange = (event, year) => {
    const isExist = expandedYear.includes(year)
    setExpandedYear(isExist ? expandedYear.filter(y => y !== year) : [...expandedYear, year])
  }

  const handleMonthChange = (event, year, month) => {
    const isExist = expandedMonth.includes(`${year}-${month}`)
    setExpandedMonth(isExist ? expandedMonth.filter(m => m !== `${year}-${month}`) : [...expandedMonth, `${year}-${month}`])
  }

  useEffect(() => {
    filtersInitializedRef.current = false
    setExpandedYear([])
    setExpandedMonth([])
  }, [accountId, locationId, configuredServiceId])

  useEffect(() => {
    if (!hasFilters || (!servicesOptions.length && !routesOptions.length)) return

    const previousServiceIds = new Set((getValues('servicesOptions') || []).map(o => o.id))
    const previousRouteIds = new Set((getValues('routesOptions') || []).map(o => o.id))

    setValue('servicesOptions', servicesOptions)
    setValue('routesOptions', routesOptions)
    setValue('routeCounts', routeCounts)

    if (!filtersInitializedRef.current) {
      setValue('selectedServices', servicesOptions)
      setValue('selectedRoutes', routesOptions)
      filtersInitializedRef.current = true
    } else {
      // Remap onto the latest option objects so labels and counts refresh without losing the selection.
      const currentSelectedServices = getValues('selectedServices') || []
      const currentSelectedRoutes = getValues('selectedRoutes') || []

      const serviceOptionsById = new Map(servicesOptions.map(option => [option.id, option]))
      const routeOptionsById = new Map(routesOptions.map(option => [option.id, option]))

      const validServices = currentSelectedServices.map(s => serviceOptionsById.get(s.id)).filter(Boolean)
      const validRoutes = currentSelectedRoutes.map(r => routeOptionsById.get(r.id)).filter(Boolean)

      const newlyDiscoveredServices = servicesOptions.filter(o => !previousServiceIds.has(o.id))
      const newlyDiscoveredRoutes = routesOptions.filter(o => !previousRouteIds.has(o.id))

      const mergedServices = validServices.length > 0 ? [...validServices, ...newlyDiscoveredServices] : servicesOptions
      const mergedRoutes = validRoutes.length > 0 ? [...validRoutes, ...newlyDiscoveredRoutes] : routesOptions

      setValue('selectedServices', mergedServices)
      setValue('selectedRoutes', mergedRoutes)
    }
  }, [hasFilters, servicesOptions, routesOptions, routeCounts])

  useEffect(() => {
    if (!expandedYear.length && !expandedMonth.length) {
      const { year = '', month = '' } = getCurrentYearAndMonthFromPeriods(periods)
      setExpandedYear(year ? [year] : [])
      setExpandedMonth(month ? [month] : [])
    }
  }, [periods])

  useEffect(() => {
    if (hasFilters) {
      const hasDateRange = selectedDateRange && selectedDateRange[0] && selectedDateRange[1]

      let year = ''
      let month = ''

      if (hasDateRange) {
        // If date range selected, use latest month in filtered results
        const latestYearMonth = getLatestYearAndMonthFromPeriods(periods)
        year = latestYearMonth.year || ''
        month = latestYearMonth.month || ''
      } else {
        // If no date range selected, try current month first
        const currentYearMonth = getCurrentYearAndMonthFromPeriods(periods)
        year = currentYearMonth.year || ''
        month = currentYearMonth.month || ''

        // If current month has no data, fallback to closest (latest) available month
        if (!year) {
          const latestYearMonth = getLatestYearAndMonthFromPeriods(periods)
          year = latestYearMonth.year || ''
          month = latestYearMonth.month || ''
        }
      }

      if (year) {
        setExpandedYear([year])
        setExpandedMonth(month ? [month] : [])
      }
    }
  }, [selectedServices, selectedRoutes, selectedWorkOrderStatuses, selectedDateRange, hasFilters, periods])

  useEffect(() => {
    getDisposalSites()
  }, [])

  return (
    <>
      {isLoading && <WorkOrderSkeleton rows={1} />}

      {!isLoading && isError && <HHSectionPlaceholder title={T.FAILED_TO_LOAD_WORK_ORDERS} />}

      {!isLoading && !isError && !hasDisplayedSummary && <HHSectionPlaceholder title={noWOText} />}

      {!isLoading && !isError && hasDisplayedSummary && (
        <Box mb={1} ml={2}>
          <Typography fontWeight={600} sx={{ color: 'text.secondary', textTransform: 'uppercase' }} variant="h5">
            {totalCount} work order
            {totalCount === 1 ? '' : 's'}
          </Typography>
        </Box>
      )}

      {!isLoading &&
        !isError &&
        hasDisplayedSummary &&
        Children.toArray(
          sortedDisplayedYears.map(year => {
            const months = periodsByYear[year]
            const isYearExpanded = expandedYear.includes(year)
            return (
              <YearAccordion key={year} isExpanded={isYearExpanded} year={year} onChange={event => handleYearChange(event, year)}>
                {Children.toArray(
                  months.map(monthNumber => {
                    const capitalizedMonth = MONTHS[monthNumber - 1]
                    const isMonthExpanded = expandedMonth.includes(`${year}-${capitalizedMonth}`)
                    return (
                      <MonthAccordion
                        key={`${year}-${capitalizedMonth}`}
                        isExpanded={isMonthExpanded}
                        month={capitalizedMonth}
                        onChange={event => handleMonthChange(event, year, capitalizedMonth)}
                      >
                        <WorkOrderList
                          key={`${year}-${capitalizedMonth}`}
                          accountId={accountId}
                          configuredServiceId={configuredServiceId}
                          disposalSites={allSites}
                          hasFilters={hasFilters}
                          isExpanded={isMonthExpanded}
                          locationId={locationId}
                          month={capitalizedMonth}
                          year={year}
                        />
                      </MonthAccordion>
                    )
                  })
                )}
              </YearAccordion>
            )
          })
        )}
    </>
  )
}

WorkOrderLayout.propTypes = {
  accountId: PropTypes.string,
  locationId: PropTypes.string,
  configuredServiceId: PropTypes.string,
  noWOText: PropTypes.string,
  hasFilters: PropTypes.bool,
}

export default WorkOrderLayout
